import { useState } from 'react';
import { createInvoiceFromWerkOrder } from '../../lib/invoices';
import type { OrderItemGroup } from '../../lib/orders';

interface CreateInvoiceDialogProps {
  werkOrderId: string;
  orderNumber: string;
  itemGroups: OrderItemGroup[];
  totalInclBtw: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (invoiceId: string, invoiceNaam: string) => void;
}

type DialogState =
  | { kind: 'preview' }
  | { kind: 'submitting' }
  | { kind: 'success'; invoiceId: string; invoiceNaam: string; lineItemsCreated: number }
  | { kind: 'already-invoiced'; existingInvoiceId: string | null; existingInvoiceNaam: string | null }
  | { kind: 'error'; message: string; code?: string };

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
}

export function CreateInvoiceDialog({
  werkOrderId,
  orderNumber,
  itemGroups,
  totalInclBtw,
  isOpen,
  onClose,
  onSuccess,
}: CreateInvoiceDialogProps) {
  const [state, setState] = useState<DialogState>({ kind: 'preview' });

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setState({ kind: 'submitting' });
    const result = await createInvoiceFromWerkOrder(werkOrderId);
    if (result.ok) {
      setState({
        kind: 'success',
        invoiceId: result.invoiceId,
        invoiceNaam: result.invoiceNaam,
        lineItemsCreated: result.lineItemsCreated,
      });
      onSuccess(result.invoiceId, result.invoiceNaam);
      return;
    }
    if (result.code === 'ALREADY_INVOICED') {
      setState({
        kind: 'already-invoiced',
        existingInvoiceId: result.existingInvoiceId ?? null,
        existingInvoiceNaam: result.existingInvoiceNaam ?? null,
      });
      return;
    }
    setState({ kind: 'error', message: result.error, code: result.code });
  };

  const close = () => {
    setState({ kind: 'preview' });
    onClose();
  };

  const lineItemCount = itemGroups.reduce(
    (acc, g) => acc + 1 + g.extras.length,
    0
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-lg bg-white shadow-xl">
        <div className="border-b px-5 py-3">
          <h2 className="text-base font-semibold text-gray-900">
            Factuur maken voor {orderNumber}
          </h2>
        </div>

        <div className="px-5 py-4">
          {state.kind === 'preview' && (
            <>
              <p className="text-sm text-gray-700">
                Er wordt direct een factuur aangemaakt op basis van de huidige
                items op deze order. De factuur-mail vertrekt automatisch
                binnen ~1 minuut naar het bekende factuur-e-mailadres.
              </p>

              <div className="mt-4 rounded border bg-gray-50">
                <div className="border-b px-3 py-2 text-xs font-medium uppercase tracking-wide text-gray-600">
                  Items ({lineItemCount})
                </div>
                <ul className="divide-y">
                  {itemGroups.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-gray-500 italic">
                      Geen items op deze order
                    </li>
                  ) : (
                    itemGroups.map((group) => (
                      <li key={group.primary.id} className="px-3 py-2 text-sm">
                        <div className="flex justify-between">
                          <span>{group.primary.name}</span>
                          <span className="tabular-nums">
                            {formatCurrency(group.primary.priceInclBtw)}
                          </span>
                        </div>
                        {group.extras.map((e) => (
                          <div
                            key={e.id}
                            className="flex justify-between pl-3 text-xs text-gray-600"
                          >
                            <span>+ {e.name}</span>
                            <span className="tabular-nums">
                              {formatCurrency(e.priceInclBtw)}
                            </span>
                          </div>
                        ))}
                      </li>
                    ))
                  )}
                </ul>
                <div className="flex justify-between border-t px-3 py-2 text-sm font-semibold">
                  <span>Totaal incl. BTW</span>
                  <span className="tabular-nums">{formatCurrency(totalInclBtw)}</span>
                </div>
              </div>
            </>
          )}

          {state.kind === 'submitting' && (
            <div className="py-6 text-center text-sm text-gray-600">
              Factuur wordt aangemaakt…
            </div>
          )}

          {state.kind === 'success' && (
            <div className="rounded border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-800">
              <div className="font-semibold">Factuur {state.invoiceNaam} aangemaakt</div>
              <div className="mt-1">
                {state.lineItemsCreated} regel{state.lineItemsCreated === 1 ? '' : 's'} aangemaakt.
                E-mail wordt binnen ~1 minuut verstuurd.
              </div>
            </div>
          )}

          {state.kind === 'already-invoiced' && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-800">
              <div className="font-semibold">
                Er bestaat al een factuur voor deze order
                {state.existingInvoiceNaam ? ` (${state.existingInvoiceNaam})` : ''}.
              </div>
              {state.existingInvoiceId && (
                <a
                  href={`/facturen/${state.existingInvoiceId}`}
                  className="mt-2 inline-block text-red-700 underline hover:text-red-900"
                >
                  Bekijk bestaande factuur
                </a>
              )}
            </div>
          )}

          {state.kind === 'error' && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
              <div className="font-semibold">Aanmaken mislukt</div>
              <div className="mt-1">{state.message}</div>
              {state.code && (
                <div className="mt-1 text-xs text-amber-700">Code: {state.code}</div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t bg-gray-50 px-5 py-3">
          {state.kind === 'preview' && (
            <>
              <button
                type="button"
                onClick={close}
                className="rounded px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Factuur aanmaken
              </button>
            </>
          )}

          {state.kind === 'submitting' && (
            <button
              type="button"
              disabled
              className="rounded bg-indigo-400 px-3 py-1.5 text-sm font-medium text-white"
            >
              Bezig…
            </button>
          )}

          {(state.kind === 'success' ||
            state.kind === 'already-invoiced' ||
            state.kind === 'error') && (
            <>
              {state.kind === 'error' && (
                <button
                  type="button"
                  onClick={() => setState({ kind: 'preview' })}
                  className="rounded px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Opnieuw
                </button>
              )}
              <button
                type="button"
                onClick={close}
                className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Sluiten
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
